import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "progress", "player", "certificate", "teamName", "draftId", "policyDialog", "invoiceDetailsDialog", "invoiceDetailsDialogFields", "donationAmount", "customDonation", "donationOption", "baseAmount", "donationTotal", "paymentTotal"]
  static values = { draftUrl: String, draftUpdateUrl: String, initialStep: Number }

  connect() {
    this.index = this.hasInitialStepValue ? this.initialStepValue : 0
    this.draftOfferingId = this.hasDraftIdTarget && this.draftIdTarget.value ? this.selectedOfferingInput()?.value : null
    this.showCurrentStep()
    this.offeringChanged()
    this.clearOfferingUrl()
    this.syncDeferredPlayers()
    this.updatePaymentSummary()
    this.syncCustomDonationVisibility()
    this.syncCertificateVisibility()
  }

  async next() {
    if (!this.currentStepIsValid()) return
    this.cancelScheduledDraftSave()
    if (this.savingDraft) await this.savingDraft

    const nextIndex = Math.min(this.index + 1, this.stepTargets.length - 1)
    this.savingDraft = this.saveDraft(nextIndex)
    let saved
    try {
      saved = await this.savingDraft
    } finally {
      this.savingDraft = null
    }
    if (!saved) return
    this.index = nextIndex
    this.showCurrentStep()
  }

  async saveDraft(step = this.index) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (!this.hasDraftIdTarget) return true

    const offering = this.selectedOfferingInput()
    if (!offering) return false

    const accessCode = this.offeringAccessCode(offering.value)
    const accessCodes = accessCode && !accessCode.disabled ? { [offering.value]: accessCode.value } : {}
    const response = await fetch(this.draftUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token, "Accept": "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ offering_id: offering.value, access_codes: accessCodes })
    })

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      this.showOfferingAccessError(offering.value, payload.error || "That entry option could not be selected.")
      return false
    }

    const { public_id: publicId } = await response.json()
    this.draftIdTarget.value = publicId
    this.draftOfferingId = offering.value

    const form = this.element.querySelector("form")
    const draftData = new FormData(form)
    draftData.set("entry[step]", step)
    const updateResponse = await fetch(this.draftUpdateUrlValue.replace("__PUBLIC_ID__", this.draftIdTarget.value), {
      method: "PATCH",
      headers: { "X-CSRF-Token": token, "Accept": "application/json" },
      body: draftData
    })

    return updateResponse.ok
  }

  back() {
    this.index = Math.max(this.index - 1, 0)
    this.showCurrentStep()
    this.saveDraft(this.index)
  }

  scheduleDraftSave() {
    if (!this.hasDraftIdTarget || !this.draftIdTarget.value) return

    this.cancelScheduledDraftSave()
    this.draftSaveTimer = window.setTimeout(() => {
      if (this.savingDraft) return

      this.savingDraft = this.saveDraft()
      this.savingDraft.finally(() => { this.savingDraft = null })
    }, 700)
  }

  cancelScheduledDraftSave() {
    if (!this.draftSaveTimer) return

    window.clearTimeout(this.draftSaveTimer)
    this.draftSaveTimer = null
  }

  setBid(event) {
    const field = this.element.querySelector("input[name='entry[bid_amount]']")
    if (field) field.value = event.currentTarget.dataset.amount
    this.updatePaymentSummary()
  }

  setDonation(event) {
    this.donationAmountTargets.forEach((field) => field.value = event.currentTarget.dataset.amount)
    this.customDonationTarget.classList.add("hidden")
    this.selectDonationOption(event.currentTarget)
    this.updatePaymentSummary()
  }

  chooseCustomDonation() {
    this.donationAmountTargets.forEach((field) => field.value = "")
    this.customDonationTarget.classList.remove("hidden")
    this.selectDonationOption(event.currentTarget)
    this.donationAmountTarget.focus()
    this.updatePaymentSummary()
  }

  selectCustomDonation() {
    this.selectDonationOption(this.donationOptionTargets.find((option) => option.dataset.donationOption === "other"))
  }

  syncCustomDonationVisibility() {
    const presetCents = [500_000, 1_000_000, 1_500_000]
    const donationCents = this.randsToCents(this.donationAmountTarget.value)
    this.customDonationTarget.classList.toggle("hidden", donationCents === 0 || presetCents.includes(donationCents))
    const selectedOption = presetCents.includes(donationCents)
      ? this.donationOptionTargets.find((option) => this.randsToCents(option.dataset.amount) === donationCents)
      : donationCents > 0 && this.donationOptionTargets.find((option) => option.dataset.donationOption === "other")
    this.selectDonationOption(selectedOption)
  }

  selectDonationOption(selectedOption) {
    this.donationOptionTargets.forEach((option) => {
      const selected = option === selectedOption
      option.classList.toggle("border-[#c71d22]", selected)
      option.classList.toggle("bg-red-50", selected)
      option.classList.toggle("text-[#c71d22]", selected)
      option.setAttribute("aria-pressed", selected)
    })
  }

  selectOffering(event) {
    const radio = event.currentTarget.querySelector("input[name='entry[offering_id]']")
    if (!radio || radio.disabled || event.target === radio) return
    if (radio.checked) return

    radio.checked = true
    radio.dispatchEvent(new Event("change", { bubbles: true }))
  }

  offeringChanged(event) {
    const selected = this.selectedOfferingInput()
    this.syncOfferingAccess(selected)
    if (!selected || !this.hasPlayerTarget) return
    const playerCount = Number(selected.dataset.playerCount) || 1
    this.playerTargets.forEach((player, index) => {
      const visible = index < playerCount
      player.classList.toggle("hidden", !visible)
      const deferToggle = player.querySelector("[data-player-defer]")
      if (deferToggle) deferToggle.disabled = !visible
      this.setPlayerDeferred(player, deferToggle?.checked || false)
    })
    const teamOffering = selected.dataset.team === "true"
    if (this.hasTeamNameTarget) this.teamNameTarget.classList.toggle("hidden", !teamOffering)
    this.element.querySelectorAll("[data-team-field]").forEach((field) => { field.required = teamOffering })
    this.element.querySelectorAll("[data-team-permission]").forEach((field) => { field.required = teamOffering })
  }

  clearOfferingUrl() {
    const url = new URL(window.location.href)
    if (!url.searchParams.has("offering_id")) return

    url.searchParams.delete("offering_id")
    window.history.replaceState({}, "", url)
  }

  syncOfferingAccess(selected = this.selectedOfferingInput()) {
    const selectedId = selected?.value
    const requiresAccessCode = selected?.dataset.requiresAccessCode === "true"

    this.element.querySelectorAll("[data-offering-access-panel]").forEach((panel) => {
      const active = panel.dataset.offeringId === selectedId
      const previouslyVerified = active && selectedId === this.draftOfferingId
      const field = panel.querySelector("input")
      const error = panel.querySelector("[data-offering-access-error]")

      panel.classList.toggle("hidden", !active || previouslyVerified)
      if (field) {
        field.disabled = !active || previouslyVerified
        field.required = active && requiresAccessCode && !previouslyVerified
        if (!active) {
          field.value = ""
          field.setCustomValidity("")
        }
      }
      if (!active && error) {
        error.classList.add("hidden")
        error.textContent = ""
      }
    })
  }

  offeringAccessCode(offeringId) {
    const panel = Array.from(this.element.querySelectorAll("[data-offering-access-panel]"))
      .find((candidate) => candidate.dataset.offeringId === offeringId)

    return panel?.querySelector("input")
  }

  selectedOfferingInput() {
    return this.element.querySelector("input[name='entry[offering_id]']:checked") ||
      this.element.querySelector("input[name='entry[offering_id]']")
  }

  showOfferingAccessError(offeringId, message) {
    const panel = Array.from(this.element.querySelectorAll("[data-offering-access-panel]"))
      .find((candidate) => candidate.dataset.offeringId === offeringId)
    const error = panel?.querySelector("[data-offering-access-error]")
    const field = panel?.querySelector("input")

    if (error) {
      error.textContent = message
      error.classList.remove("hidden")
    }
    field?.setCustomValidity(message)
  }

  clearOfferingAccessError(event) {
    const field = event.currentTarget
    const panel = field.closest("[data-offering-access-panel]")
    const error = panel?.querySelector("[data-offering-access-error]")

    field.setCustomValidity("")
    error?.classList.add("hidden")
    if (error) error.textContent = ""
  }

  togglePlayer(event) {
    const player = event.currentTarget.closest("[data-entry-flow-target='player']")
    if (player) this.setPlayerDeferred(player, event.currentTarget.checked)
  }

  syncDeferredPlayers() {
    this.playerTargets.forEach((player) => {
      const deferToggle = player.querySelector("[data-player-defer]")
      this.setPlayerDeferred(player, deferToggle?.checked || false)
    })
  }

  setPlayerDeferred(player, deferred) {
    const fields = player.querySelector("[data-player-fields]")
    const visible = !player.classList.contains("hidden")
    fields?.classList.toggle("hidden", deferred)
    fields?.querySelectorAll("[data-player-field]").forEach((field) => {
      field.disabled = !visible || deferred
      if (field.matches("[data-player-email-field]")) field.required = visible && !deferred
    })
  }

  paymentProviderChanged() {
    this.syncCertificateVisibility()
    this.saveDraft()
  }

  syncCertificateVisibility() {
    if (!this.hasCertificateTarget) return

    this.certificateTarget.classList.remove("hidden")
    this.certificateTarget.querySelectorAll("[data-certificate-field]").forEach((field) => { field.required = false })
  }

  validateSubmission(event) {
    const field = Array.from(this.element.querySelectorAll("input, select, textarea"))
      .find((candidate) => !candidate.disabled && !candidate.checkValidity())
    if (field) {
      event.preventDefault()
      const step = field.closest("[data-entry-flow-target='step']")
      const stepIndex = this.stepTargets.indexOf(step)
      if (stepIndex >= 0 && stepIndex !== this.index) {
        this.index = stepIndex
        this.showCurrentStep()
      }
      window.setTimeout(() => {
        field.focus()
        field.reportValidity()
      }, 0)
      return
    }

    if (this.invoiceDetailsRequired()) {
      event.preventDefault()
      this.openInvoiceDetails()
    }
  }

  updatePaymentSummary() {
    // This is a preview only; the server recalculates the final PayFast total.
    const selected = this.selectedOfferingInput()
    const bid = this.element.querySelector("input[name='entry[bid_amount]']")
    const baseCents = bid ? this.randsToCents(bid.value) : Number(selected?.dataset.priceCents || 0)
    const donationCents = this.hasDonationAmountTarget ? this.randsToCents(this.donationAmountTarget.value) : 0

    this.baseAmountTargets.forEach((target) => target.textContent = this.formatCurrency(baseCents))
    this.donationTotalTargets.forEach((target) => target.textContent = this.formatCurrency(donationCents))
    this.paymentTotalTargets.forEach((target) => target.textContent = this.formatCurrency(baseCents + donationCents))
  }

  randsToCents(value) {
    const amount = Number.parseFloat(value)
    return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0
  }

  formatCurrency(cents) {
    return `R${(cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  openPolicy(event) {
    const dialog = this.policyDialogTargets.find((candidate) => candidate.dataset.policy === event.currentTarget.dataset.policy)
    dialog?.showModal()
  }

  closePolicy(event) {
    event.currentTarget.closest("dialog")?.close()
  }

  closePolicyOnBackdrop(event) {
    if (event.target === event.currentTarget) event.currentTarget.close()
  }

  invoiceDetailsRequired() {
    const paymentProvider = this.element.querySelector("input[name='entry[payment_provider]']:checked")
    if (paymentProvider?.value !== "eft") return false

    return this.certificateFields().some((field) => field.value.trim() === "")
  }

  certificateFields() {
    return Array.from(this.element.querySelectorAll("[data-certificate-field]"))
  }

  openInvoiceDetails() {
    if (!this.hasInvoiceDetailsDialogTarget || !this.hasInvoiceDetailsDialogFieldsTarget) return

    Array.from(this.certificateTarget.children).forEach((fieldGroup) => this.invoiceDetailsDialogFieldsTarget.append(fieldGroup))
    this.certificateFields().forEach((field) => { field.required = true })
    this.invoiceDetailsDialogTarget.showModal()
    window.setTimeout(() => this.certificateFields().find((field) => field.value.trim() === "")?.focus(), 0)
  }

  saveInvoiceDetails() {
    const invalidField = this.certificateFields().find((field) => !field.checkValidity())
    if (invalidField) {
      invalidField.reportValidity()
      return
    }

    this.restoreInvoiceDetails()
    this.invoiceDetailsDialogTarget.close()
    this.element.querySelector("form")?.requestSubmit()
  }

  cancelInvoiceRequest() {
    this.restoreInvoiceDetails()
    this.invoiceDetailsDialogTarget.close()
    this.element.querySelector("input[name='entry[payment_provider]']:checked")?.focus()
  }

  preventInvoiceDetailsDismissal(event) {
    event.preventDefault()
  }

  closeInvoiceDetailsOnBackdrop(event) {
    if (event.target === event.currentTarget) event.preventDefault()
  }

  restoreInvoiceDetails() {
    if (!this.hasCertificateTarget || !this.hasInvoiceDetailsDialogFieldsTarget) return

    Array.from(this.invoiceDetailsDialogFieldsTarget.children).forEach((fieldGroup) => this.certificateTarget.append(fieldGroup))
    this.certificateFields().forEach((field) => { field.required = false })
  }

  currentStepIsValid() {
    const fields = this.stepTargets[this.index].querySelectorAll("input, select, textarea")
    for (const field of fields) {
      if (!field.disabled && !field.checkValidity()) {
        field.reportValidity()
        return false
      }
    }
    return true
  }

  showCurrentStep() {
    this.stepTargets.forEach((step, index) => step.classList.toggle("hidden", index !== this.index))
    this.progressTargets.forEach((progress, index) => {
      progress.classList.toggle("border-[#c71d22]", index <= this.index)
      progress.classList.toggle("text-[#c71d22]", index === this.index)
    })
    this.element.scrollIntoView({ block: "start", behavior: "smooth" })
  }
}
